import { useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'

import {
  selectWizardIsVisible,
  selectWizardSaveDisabled,
  setWizardSaveDisabled,
} from '../features/planningWizard'

type SaveHandler = () => Promise<void>

let saveHandlerRef: SaveHandler | null = null

export const useWizardSave = () => {
  const dispatch = useAppDispatch()
  const isVisible = useSelector(selectWizardIsVisible)
  const isSaveDisabled = useSelector(selectWizardSaveDisabled)
  const localSaveRef = useRef<SaveHandler | null>(null)

  const registerSaveHandler = useCallback(
    (handler: SaveHandler, disabled: boolean) => {
      saveHandlerRef = handler
      localSaveRef.current = handler
      dispatch(setWizardSaveDisabled(disabled))
    },
    [dispatch]
  )

  const updateDisabled = useCallback(
    (disabled: boolean) => {
      dispatch(setWizardSaveDisabled(disabled))
    },
    [dispatch]
  )

  const unregisterSaveHandler = useCallback(() => {
    if (saveHandlerRef === localSaveRef.current) {
      saveHandlerRef = null
      dispatch(setWizardSaveDisabled(true))
    }
    localSaveRef.current = null
  }, [dispatch])

  const save = useCallback(async () => {
    if (saveHandlerRef) {
      await saveHandlerRef()
    }
  }, [])

  useEffect(() => {
    return () => {
      if (saveHandlerRef === localSaveRef.current) {
        saveHandlerRef = null
      }
    }
  }, [])

  if (!isVisible) {
    return null
  }

  return {
    registerSaveHandler,
    updateDisabled,
    unregisterSaveHandler,
    save,
    isSaveDisabled,
    hasSaveHandler: saveHandlerRef !== null,
  }
}
